import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import { FiCopy, FiImage } from 'react-icons/fi';
import GodViewMediaModal from './GodViewMediaModal.jsx';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
};

const formatAmount = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `$${Number(amount).toFixed(2)}`;
};

// Format renewal date as DDMMYY
const formatRenewalDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}${month}${year}`;
};

// Format renewal date as MmmYY (e.g., "Oct22", "Nov25", "Jan26")
const formatRenewalDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${month}${year}`;
};

const sameCalendarDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a);
    const db = new Date(b);
    if (!Number.isFinite(da.getTime()) || !Number.isFinite(db.getTime())) return false;
    return (
        da.getFullYear() === db.getFullYear()
        && da.getMonth() === db.getMonth()
        && da.getDate() === db.getDate()
    );
};

const memberSummaryFirstLine = (member) => {
    const groupName = String(member.lastGodGroup?.groupName || '').trim();
    const showGroup = Boolean(
        groupName && sameCalendarDay(member.lastGodGroup?.renewalDate, member.renewalDate)
    );
    const left = [member.godID, formatRenewalDate(member.renewalDate), showGroup ? groupName : '']
        .filter((part) => String(part || '').trim() !== '')
        .join(' ');
    return `${left} - ${member.godName} - ${member.webID} ${member.nationality || ''}`.trim();
};

// Copy to clipboard function
const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        console.log('Copied to clipboard:', text);
    } catch (err) {
        console.error('Failed to copy: ', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    }
};

const getRenewalStatusStyle = (renewalDate) => {
    if (!renewalDate) return 'text-gray-600';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const renewalDateTime = new Date(renewalDate);
    renewalDateTime.setHours(0, 0, 0, 0);
    
    if (renewalDateTime <= today) {
        return 'text-red-600 font-bold';
    }
    return 'text-green-600 font-bold';
};

const ErrorFallback = ({ error }) => (
    <div className="text-red-600 p-4 bg-red-50 rounded-lg">
        <h4 className="font-medium">Something went wrong:</h4>
        <pre className="text-sm">{error.message}</pre>
    </div>
);

const MemberDetailsContent = memo(({ member, onClose, onOpenMedia }) => {
    const [websiteCopied, setWebsiteCopied] = useState(false);

    useEffect(() => {
        if (!websiteCopied) return;
        const t = setTimeout(() => setWebsiteCopied(false), 2000);
        return () => clearTimeout(t);
    }, [websiteCopied]);

    if (!member) return null;

    const hasLastGodGroup = member.lastGodGroup && (String(member.lastGodGroup.groupName ?? '').trim() !== '' || member.lastGodGroup._id);
    const summaryFirstLine = memberSummaryFirstLine(member);

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center pt-20 px-4 pb-4 overflow-x-hidden overflow-y-auto min-h-full">
            <div className="relative mx-auto p-4 border max-w-3xl w-full shadow-lg rounded-md bg-white overflow-hidden flex flex-col max-h-[calc(100vh-5rem)]">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-xl font-medium">
                            Member Details - <span className="ml-2 text-blue-700">{member.fullName}</span>
                        </h3>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                            ${member.status === 'active' ? 'bg-green-100 text-green-800' : 
                              member.status === 'expired' ? 'bg-red-100 text-red-800' :
                              member.status === 'hold' ? 'bg-yellow-100 text-yellow-800' :
                              member.status === 'stop' ? 'bg-rose-100 text-rose-800' :
                              'bg-gray-100 text-gray-800'}`}>
                            {member.status}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-sm ${getRenewalStatusStyle(member.renewalDate)}`}>
                            Renewal: {formatDate(member.renewalDate)}
                        </span>
                        {onOpenMedia && (
                            <button
                                type="button"
                                onClick={onOpenMedia}
                                className="flex items-center gap-1.5 px-2 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                                <FiImage className="w-4 h-4" />
                                Media
                            </button>
                        )}
                        <button 
                            onClick={onClose} 
                            className="text-gray-500 hover:text-gray-700"
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="mt-3 space-y-3 overflow-y-auto flex-1 min-h-0">
                    {/* First row: Member Summary 70%, Last God Group 30% */}
                    <section className="flex gap-3 flex-1 min-w-0">
                        <div className="bg-gray-50 p-2 rounded-md min-w-0 flex-[7]">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="text-xs font-semibold text-gray-900">Member Summary</h3>
                                <button
                                    onClick={() => {
                                        const firstLine = summaryFirstLine;
                                        const secondLine = `${formatRenewalDateShort(member.renewalDate)} ${member.shop?.[0]?.shopName || 'N/A'} | ${member.shop?.[0]?.address || 'N/A'}`;
                                        copyToClipboard(`${firstLine}\n${secondLine}`);
                                    }}
                                    className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                    <FiCopy className="w-2.5 h-2.5" />
                                    Copy All
                                </button>
                            </div>
                            <div className="space-y-1 text-xs font-mono text-gray-800 break-words">
                                <div className="flex items-center gap-1">
                                    <button onClick={() => copyToClipboard(summaryFirstLine)} className="px-1 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200 shrink-0"><FiCopy className="w-2.5 h-2.5 inline" /></button>
                                    <span>{summaryFirstLine}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => copyToClipboard(`${formatRenewalDateShort(member.renewalDate)} ${member.shop?.[0]?.shopName || 'N/A'} | ${member.shop?.[0]?.address || 'N/A'}`)} className="px-1 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200 shrink-0"><FiCopy className="w-2.5 h-2.5 inline" /></button>
                                    <span>{formatRenewalDateShort(member.renewalDate)} {member.shop?.[0]?.shopName || 'N/A'} | {member.shop?.[0]?.address || 'N/A'}</span>
                                </div>
                                {member.website && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => { copyToClipboard(member.website); setWebsiteCopied(true); }}
                                            className="px-1 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200 shrink-0"
                                        >
                                            <FiCopy className="w-2.5 h-2.5 inline" />
                                        </button>
                                        <span className="min-w-0 truncate">{member.website}</span>
                                        {websiteCopied && <span className="text-xs font-medium text-green-600 shrink-0">Copied</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded-md min-w-0 flex-[3]">
                            <h3 className="text-xs font-semibold text-gray-900 mb-1">Last God Group</h3>
                            {hasLastGodGroup ? (
                                <div className="text-xs text-blue-700 break-words">
                                    <span className="font-medium">{member.lastGodGroup.groupName}</span>
                                    <span className="text-gray-600 ml-1">Renewal: {member.lastGodGroup.renewalDate ? formatDate(member.lastGodGroup.renewalDate) : 'N/A'}</span>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-500">No god group</p>
                            )}
                        </div>
                    </section>

                    {/* Basic Information: row1 God ID, Name, Web ID, Nationality, Type | row2 oldNames | row3 Referred By, Age, UpdatePhoto, Update Contact (aligned) */}
                    <section>
                        <h4 className="text-sm font-medium mb-1">Basic Information</h4>
                        <div className="bg-gray-50 p-2 rounded-lg text-xs">
                            <div className="grid grid-cols-5 gap-x-4 gap-y-1 items-baseline">
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">God ID:</span><span className="text-blue-700">{member.godID}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">Name:</span><span className="text-blue-700">{member.godName}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">Web ID:</span><span className="text-blue-700">{member.webID}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">Nationality:</span><span className="text-blue-700">{member.nationality || 'N/A'}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">Type:</span><span className="text-blue-700">{member.memberType || 'Member'}</span></div>
                                <div className="col-span-5 flex flex-wrap gap-x-4 gap-y-0 items-baseline min-w-0"><span className="text-gray-600 shrink-0">Old Names:</span><span className="text-blue-700 min-w-0">{member.oldNames?.length ? member.oldNames.join(', ') : 'N/A'}</span><span className="text-gray-600 shrink-0">SBF ID:</span><span className="text-blue-700 min-w-0 truncate">{member.sbfID?.length ? member.sbfID.join(', ') : 'N/A'}</span></div>
                                <div className="col-span-2 min-w-0"><span className="text-gray-600 inline-block w-20 shrink-0">Referred By:</span><span className="text-blue-700 break-words">{member.referredBy?.fullName || (member.referredBy && [member.referredBy.godID, member.referredBy.godName, member.referredBy.webID].filter(Boolean).join(' - ')) || 'N/A'}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">Age:</span><span className="text-blue-700">{member.age ? new Date(member.age).toLocaleDateString() : 'N/A'}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">upPhoto:</span><span className="text-blue-700">{member.photos ? '✅' : '❌'}</span></div>
                                <div className="col-span-1"><span className="text-gray-600 inline-block w-20 shrink-0">upContact:</span><span className="text-blue-700">{member.updateContact ? '✅' : '❌'}</span></div>
                            </div>
                            <div className="mt-1"><span className="text-gray-600 inline-block w-20 shrink-0">Autobot:</span><span className="text-blue-700">{member.autobot ? '✅' : '❌'}</span></div>
                            <div className="mt-1"><span className="text-gray-600 inline-block w-20 shrink-0">Shop:</span><span className="text-blue-700">{member.shop && member.shop.length > 0 ? member.shop.map(s => `${s.shopName}${s.region || s.area ? ` (${[s.region, s.area].filter(Boolean).join(', ')})` : ''}${s.address ? ` · ${s.address}` : ''}`).join('; ') : 'N/A'}</span>{member.hideForShop && <span className="text-red-600 ml-1">hide for shop</span>}</div>
                            {(member.openHour != null && member.openHour !== '') || (member.closeHour != null && member.closeHour !== '') ? (
                                <div className="mt-0.5"><span className="text-gray-600 inline-block w-20 shrink-0">Hours:</span><span className="text-blue-700">{member.openHour || '—'} – {member.closeHour || '—'}</span></div>
                            ) : null}
                            {member.remarks && <div className="mt-0.5"><span className="text-gray-600 inline-block w-20 shrink-0">Remarks:</span><span className="text-blue-700">{member.remarks}</span></div>}
                            {member.priceHr != null && member.priceHr !== '' && Number.isFinite(Number(member.priceHr)) && (
                                <div className="mt-0.5"><span className="text-gray-600 inline-block w-24 shrink-0">Price / hr:</span><span className="text-blue-700">{Number(member.priceHr)}</span></div>
                            )}
                            {Array.isArray(member.service) && member.service.length > 0 && (
                                <div className="mt-0.5"><span className="text-gray-600 inline-block w-24 shrink-0">Service:</span><span className="text-blue-700">{member.service.join(', ')}</span></div>
                            )}
                            {member.extraService && String(member.extraService).trim() && (
                                <div className="mt-0.5 flex gap-1"><span className="text-gray-600 inline-block w-24 shrink-0 align-top">Extra:</span><span className="text-blue-700 break-words whitespace-pre-wrap min-w-0 flex-1">{String(member.extraService).trim()}</span></div>
                            )}
                            {member.websiteRemarks && String(member.websiteRemarks).trim() && (
                                <div className="mt-0.5 flex gap-1"><span className="text-gray-600 inline-block w-24 shrink-0 align-top">Web remarks:</span><span className="text-blue-700 break-words whitespace-pre-wrap min-w-0 flex-1">{String(member.websiteRemarks).trim()}</span></div>
                            )}
                        </div>
                    </section>

                    {/* Contact Information: row1 ContactNumber, WhatsAppNumber | row2 TGID, Username, Number, LastPost (label+value per row) */}
                    <section>
                        <h4 className="text-sm font-medium mb-1">Contact Information</h4>
                        <div className="bg-gray-50 p-2 rounded-lg text-xs space-y-2">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                <div>
                                    <span className="text-gray-600 block">Contact Number</span>
                                    {member.contactNumbers?.length ? (
                                        member.contactNumbers.map((entry, i) => {
                                            const num = typeof entry === 'string' ? entry : entry?.contactNumber;
                                            const isActive = typeof entry !== 'object' || entry?.isActive !== false;
                                            return num ? <div key={i} className="text-blue-700">{num} {isActive ? '✅' : '❌'}</div> : null;
                                        })
                                    ) : <span className="text-blue-700">N/A</span>}
                                </div>
                                <div>
                                    <span className="text-gray-600 block">WhatsApp Number</span>
                                    {member.whatsAppNumbers?.length ? (
                                        member.whatsAppNumbers.map((entry, i) => {
                                            const num = typeof entry === 'string' ? entry : entry?.whatsAppNumber;
                                            const isActive = typeof entry !== 'object' || entry?.isActive !== false;
                                            return num ? <div key={i} className="text-blue-700">{num} {isActive ? '✅' : '❌'}</div> : null;
                                        })
                                    ) : <span className="text-blue-700">N/A</span>}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600 block mb-1">Telegram Account</span>
                                {member.godTGs && member.godTGs.length > 0 ? (
                                    <div className="space-y-1">
                                        {member.godTGs.map((tg, index) => {
                                            let lastPostDate = 'N/A';
                                            let totalCount = 0;
                                            if (tg.postHistory && Array.isArray(tg.postHistory) && tg.postHistory.length > 0 && tg.postHistory[0].groupPosts) {
                                                const sorted = [...tg.postHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
                                                const latest = sorted[0];
                                                if (latest?.date) {
                                                    lastPostDate = new Date(latest.date).toLocaleDateString();
                                                    totalCount = latest.groupPosts.reduce((sum, p) => sum + (p.count || 0), 0);
                                                }
                                            }
                                            return (
                                                <div key={index} className="grid grid-cols-4 gap-x-2 gap-y-0 items-baseline border-b border-gray-200 pb-1 last:border-0 last:pb-0">
                                                    <div><span className="text-gray-600">TGID:</span> <span className="text-blue-700">{tg.godTGID || '—'}</span></div>
                                                    <div><span className="text-gray-600">Username:</span> <span className="text-blue-700">{tg.godTGUsername ? `@${tg.godTGUsername}` : '—'}</span></div>
                                                    <div><span className="text-gray-600">Number:</span> <span className="text-blue-700">{tg.godTGNumber || '—'}</span></div>
                                                    <div><span className="text-gray-600">Last Post:</span> <span className="text-blue-700">{lastPostDate}{totalCount > 0 ? ` (${totalCount})` : ''}</span>{tg.notInUse && <span className="text-amber-600 ml-1">Not in use</span>}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <span className="text-blue-700">N/A</span>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Payment Information */}
                    <section>
                        <h4 className="text-sm font-medium mb-1">Payment Information</h4>
                        <div className="bg-gray-50 p-2 rounded-lg space-y-2">
                            {member.paymentHistory === undefined ? (
                                <div className="text-gray-500 py-2 text-center text-xs">Loading payment information…</div>
                            ) : (
                            <>
                            <div className="grid grid-cols-2 gap-2">
                                {/* Last Payment - left column */}
                                <div className="bg-white p-2 rounded text-xs">
                                    <h5 className="font-medium mb-1">Last Payment</h5>
                                    {member.lastPaymentInfo ? (
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="text-blue-700">Amount: {formatAmount(member.lastPaymentInfo.paymentAmount)}</span>
                                            <span className="text-blue-700">Date: {formatDate(member.lastPaymentInfo.paymentDate)}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">N/A</span>
                                    )}
                                </div>

                                {/* Total Payment - right column */}
                                <div className="bg-white p-2 rounded text-xs">
                                    <h5 className="font-medium mb-1">Total Payment</h5>
                                {(() => {
                                    let totalAmount = 0;

                                    // Calculate individual vs misc fees (both stored on member.payments)
                                    const individualTotal = member.payments?.reduce((sum, payment) =>
                                        payment.paymentType === 'misc_fees' ? sum : sum + (payment.paymentAmount || 0), 0) || 0;
                                    const miscTotal = member.payments?.reduce((sum, payment) =>
                                        payment.paymentType === 'misc_fees' ? sum + (payment.paymentAmount || 0) : sum, 0) || 0;

                                    // Calculate group payments (both as handler and participant)
                                    const groupPayments = new Map(); // To deduplicate payments
                                    
                                    // Process handler payments
                                    member.handlerPayments?.forEach(payment => {
                                        const key = `${payment.godGroup?._id}-${payment.paymentDate}`;
                                        if (!groupPayments.has(key) && (payment.paymentType === 'group' || payment.paymentType === 'add_on')) {
                                            const participants = payment.godGroup?.groupMembers?.participants || [];
                                            const paymentDate = new Date(payment.paymentDate);
                                            
                                            // Find members who joined exactly on payment date
                                            const matchingMembers = participants.filter(p => {
                                                const joinDate = new Date(p.joinDate);
                                                return joinDate.toDateString() === paymentDate.toDateString();
                                            });

                                            const isMatchingMember = matchingMembers.some(p => p.memberID?._id === member._id);
                                            groupPayments.set(key, isMatchingMember ? payment.paymentAmount / matchingMembers.length : 0);
                                        }
                                    });

                                    // Process participant payments if not already shown
                                    member.participantPayments?.forEach(payment => {
                                        const key = `${payment.godGroup?._id}-${payment.paymentDate}`;
                                        if (!groupPayments.has(key) && (payment.paymentType === 'group' || payment.paymentType === 'add_on')) {
                                            const participants = payment.godGroup?.groupMembers?.participants || [];
                                            const paymentDate = new Date(payment.paymentDate);
                                            
                                            // Find members who joined exactly on payment date
                                            const matchingMembers = participants.filter(p => {
                                                const joinDate = new Date(p.joinDate);
                                                return joinDate.toDateString() === paymentDate.toDateString();
                                            });

                                            const isMatchingMember = matchingMembers.some(p => p.memberID?._id === member._id);
                                            groupPayments.set(key, isMatchingMember ? payment.paymentAmount / matchingMembers.length : 0);
                                        }
                                    });

                                    // Sum up all group payment shares
                                    const groupTotal = Array.from(groupPayments.values()).reduce((sum, amount) => sum + amount, 0);

                                    // Calculate final total (rounded to 2 decimal places)
                                    totalAmount = Number((individualTotal + miscTotal + groupTotal).toFixed(2));

                                    return (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-semibold text-green-700">Total: {formatAmount(totalAmount)}</span>
                                            <span className="text-gray-600">(Individual: {formatAmount(individualTotal)}{miscTotal > 0 ? ` + Misc: ${formatAmount(miscTotal)}` : ''} + Group: {formatAmount(groupTotal)})</span>
                                        </div>
                                    );
                                })()}
                                </div>
                            </div>

                            <div>
                                <h5 className="font-medium mb-1">Payment History</h5>
                                <div className="space-y-1">
                                    {(member.payments || member.handlerPayments || member.participantPayments) && 
                                    (member.payments?.length > 0 || member.handlerPayments?.length > 0 || member.participantPayments?.length > 0) ? (
                                        <>
                                            {/* Individual Payments */}
                                            {member.payments?.map((payment, index) => {
                                                const isMisc = payment.paymentType === 'misc_fees';
                                                return (
                                                <div key={`member-pay-${index}`} className="flex flex-wrap items-center gap-2 bg-white p-1.5 rounded text-xs">
                                                    <span className={`px-1.5 py-0.5 font-semibold rounded-full ${isMisc ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                        {isMisc ? 'Misc Fees' : 'Individual'}
                                                    </span>
                                                    <span className="text-blue-700">Amount: {formatAmount(payment.paymentAmount)}</span>
                                                    <span className="text-blue-700">Date: {formatDate(payment.paymentDate)}</span>
                                                    {payment.paymentRemarks && <span className="text-gray-600">Remarks: {payment.paymentRemarks}</span>}
                                                </div>
                                                );
                                            })}
                                            
                                            {/* Group and Add-on Payments */}
                                            {(() => {
                                                // Create a map to track shown payments by group and date
                                                const shownPayments = new Map();
                                                
                                                // Process handler payments first
                                                member.handlerPayments?.forEach(payment => {
                                                    const key = `${payment.godGroup?._id}-${payment.paymentDate}`;
                                                    if (payment.paymentType === 'group' || payment.paymentType === 'add_on') {
                                                        const participants = payment.godGroup?.groupMembers?.participants || [];
                                                        const paymentDate = new Date(payment.paymentDate);
                                                        
                                                        // Find members who joined exactly on payment date
                                                        const matchingMembers = participants.filter(p => {
                                                            const joinDate = new Date(p.joinDate);
                                                            return joinDate.toDateString() === paymentDate.toDateString();
                                                        });

                                                        const isMatchingMember = matchingMembers.some(p => p.memberID?._id === member._id);
                                                        const share = isMatchingMember ? payment.paymentAmount / matchingMembers.length : 0;

                                                        shownPayments.set(key, {
                                                            payment,
                                                            type: payment.paymentType,
                                                            memberCount: matchingMembers.length,
                                                            share,
                                                            description: `Group: ${payment.godGroup.groupName} (${matchingMembers.length} member${matchingMembers.length !== 1 ? 's' : ''} joined on ${formatDate(payment.paymentDate)})`
                                                        });
                                                    }
                                                });

                                                // Process participant payments if not already shown
                                                member.participantPayments?.forEach(payment => {
                                                    const key = `${payment.godGroup?._id}-${payment.paymentDate}`;
                                                    if (!shownPayments.has(key) && (payment.paymentType === 'group' || payment.paymentType === 'add_on')) {
                                                        const participants = payment.godGroup?.groupMembers?.participants || [];
                                                        const paymentDate = new Date(payment.paymentDate);
                                                        
                                                        // Find members who joined exactly on payment date
                                                        const matchingMembers = participants.filter(p => {
                                                            const joinDate = new Date(p.joinDate);
                                                            return joinDate.toDateString() === paymentDate.toDateString();
                                                        });

                                                        const isMatchingMember = matchingMembers.some(p => p.memberID?._id === member._id);
                                                        const share = isMatchingMember ? payment.paymentAmount / matchingMembers.length : 0;

                                                        shownPayments.set(key, {
                                                            payment,
                                                            type: payment.paymentType,
                                                            memberCount: matchingMembers.length,
                                                            share,
                                                            description: `Group: ${payment.godGroup.groupName} (${matchingMembers.length} member${matchingMembers.length !== 1 ? 's' : ''} joined on ${formatDate(payment.paymentDate)})`
                                                        });
                                                    }
                                                });

                                                // Convert map to array, keep only entries where member has a share (related to this member), then sort by date
                                                return Array.from(shownPayments.values())
                                                    .filter(({ share }) => share > 0)
                                                    .sort((a, b) => new Date(b.payment.paymentDate) - new Date(a.payment.paymentDate))
                                                    .map(({ payment, type, share, description }, index) => (
                                                        <div key={`${type}-${index}`} className="flex flex-wrap items-center gap-2 bg-white p-1.5 rounded text-xs">
                                                            <span className={`px-1.5 py-0.5 font-semibold rounded-full ${type === 'group' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                {type === 'group' ? 'Group' : 'Add-on'}
                                                            </span>
                                                            <span className="text-gray-800">{description}</span>
                                                            <span className="text-blue-700">Total: {formatAmount(payment.paymentAmount)}</span>
                                                            <span className={share > 0 ? "text-green-600" : "text-gray-500"}>Share: {formatAmount(share)}</span>
                                                            <span className="text-blue-700">Date: {formatDate(payment.paymentDate)}</span>
                                                            {payment.paymentRemarks && <span className="text-gray-600">Remarks: {payment.paymentRemarks}</span>}
                                                        </div>
                                                    ));
                                            })()}
                                        </>
                                    ) : (
                                        <p className="text-gray-500 text-xs">No payment history available</p>
                                    )}
                                </div>
                            </div>
                            </>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
});

const MemberDetailsModal = ({ member, onClose, detailsLoading = false, onMemberUpdated }) => {
    const [showMediaModal, setShowMediaModal] = useState(false);

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <div className="relative">
                <MemberDetailsContent
                    member={member}
                    onClose={onClose}
                    onOpenMedia={member?._id ? () => setShowMediaModal(true) : undefined}
                />
                {detailsLoading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                        <div className="text-center text-gray-600">
                            <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                            <p className="text-sm font-medium">Loading payment history &amp; details…</p>
                        </div>
                    </div>
                )}
            </div>
            {showMediaModal && member && (
                <GodViewMediaModal
                    member={member}
                    onClose={() => setShowMediaModal(false)}
                    onMediaUpdate={onMemberUpdated}
                />
            )}
        </ErrorBoundary>
    );
};

MemberDetailsModal.propTypes = {
    member: PropTypes.shape({
        fullName: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        renewalDate: PropTypes.string,
        godID: PropTypes.string.isRequired,
        godName: PropTypes.string.isRequired,
        webID: PropTypes.string,
        memberType: PropTypes.string,
        nationality: PropTypes.string,
        age: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        remarks: PropTypes.string,
        priceHr: PropTypes.number,
        service: PropTypes.arrayOf(PropTypes.string),
        extraService: PropTypes.string,
        websiteRemarks: PropTypes.string,
        contactNumbers: PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({ contactNumber: PropTypes.string, isActive: PropTypes.bool })
        ])),
        whatsAppNumbers: PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({ whatsAppNumber: PropTypes.string, isActive: PropTypes.bool })
        ])),
        photos: PropTypes.bool,
        updateContact: PropTypes.bool,
        oldNames: PropTypes.arrayOf(PropTypes.string),
        godTGs: PropTypes.arrayOf(PropTypes.shape({
            godTGID: PropTypes.string.isRequired,
            godTGUsername: PropTypes.string,
            godTGNumber: PropTypes.string,
            notInUse: PropTypes.bool
        })),
        lastGodGroup: PropTypes.shape({
            groupName: PropTypes.string.isRequired,
            renewalDate: PropTypes.string
        }),
        lastPaymentInfo: PropTypes.shape({
            paymentAmount: PropTypes.number.isRequired,
            paymentDate: PropTypes.string.isRequired
        }),
        payments: PropTypes.arrayOf(PropTypes.shape({
            paymentAmount: PropTypes.number.isRequired,
            paymentDate: PropTypes.string.isRequired,
            paymentRemarks: PropTypes.string
        })),
        handlerPayments: PropTypes.arrayOf(PropTypes.shape({
            paymentAmount: PropTypes.number.isRequired,
            paymentDate: PropTypes.string.isRequired,
            paymentRemarks: PropTypes.string,
            paymentType: PropTypes.string.isRequired,
            godGroup: PropTypes.shape({
                groupName: PropTypes.string.isRequired,
                groupMembers: PropTypes.shape({
                    participants: PropTypes.arrayOf(PropTypes.shape({
                        removeDate: PropTypes.string
                    }))
                })
            })
        })),
        participantPayments: PropTypes.arrayOf(PropTypes.shape({
            paymentAmount: PropTypes.number.isRequired,
            paymentDate: PropTypes.string.isRequired,
            paymentRemarks: PropTypes.string,
            paymentType: PropTypes.string.isRequired,
            godGroup: PropTypes.shape({
                groupName: PropTypes.string.isRequired,
                groupMembers: PropTypes.shape({
                    participants: PropTypes.arrayOf(PropTypes.shape({
                        removeDate: PropTypes.string
                    }))
                })
            }),
            handler: PropTypes.shape({
                godName: PropTypes.string.isRequired,
                godID: PropTypes.string.isRequired
            })
        })),
        shop: PropTypes.arrayOf(PropTypes.shape({
            shopName: PropTypes.string.isRequired,
            region: PropTypes.string.isRequired,
            area: PropTypes.string.isRequired,
            address: PropTypes.string.isRequired
        })),
        referredBy: PropTypes.shape({
            fullName: PropTypes.string.isRequired
        })
    }),
    onClose: PropTypes.func.isRequired,
    detailsLoading: PropTypes.bool,
    onMemberUpdated: PropTypes.func
};

export default MemberDetailsModal; 